import React, { useContext, useEffect, useState, useRef } from "react";
import { Box, Text, Flex, Heading, Spinner, VStack } from "@chakra-ui/react";
import { UserProfileContext } from "../../context/UserContext";
import FullPageLoader from "../../components/fullPageLoader";
import { ProgressSteps } from "../topUp/components/Steps";
import { useDispatch } from "react-redux";
import { getCommodityColor } from "../../utils/themeApplicator";
import {
  updateBrandColor,
  setCommodityRates,
  setDashboardData,
} from "../../store/actions/displayAction";
import { getResolvedUserType, USER_TYPES } from "../../utils/userRole";
import DashboardBanner from "./components/DashboardBanner";
import { VTUFeature } from "./components/VTUFeature";
import { getDashboardData } from "./services/getDashboardData";
import {
  COMMODITY_SECTION_CONFIG,
  CommoditySection,
} from "./components/CommoditySection";
const DEFAULT_ALLOWED_COMMODITIES = ["water", "electricity", "gas"];

const normalizeCommodity = (value = "") => String(value).trim().toLowerCase();

const getAllowedCommodities = (userProfile = {}) => {
  const fromEstate = userProfile?.streetId?.estateId?.allowedCommodities;
  if (Array.isArray(fromEstate) && fromEstate.length > 0) {
    const normalized = fromEstate
      .map(normalizeCommodity)
      .filter((commodity) => DEFAULT_ALLOWED_COMMODITIES.includes(commodity));

    if (normalized.length > 0) {
      return [...new Set(normalized)];
    }
  }

  const commodityFlags =
    Array.isArray(userProfile?.commodityType) &&
    userProfile?.commodityType.length > 0
      ? userProfile?.commodityType?.[0]
      : userProfile?.commodityType;

  if (commodityFlags && typeof commodityFlags === "object") {
    const fromUser = DEFAULT_ALLOWED_COMMODITIES.filter(
      (commodity) => commodityFlags?.[commodity] === true,
    );

    if (fromUser.length > 0) {
      return fromUser;
    }
  }

  return DEFAULT_ALLOWED_COMMODITIES;
};

export const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [displayPage, setDisplayPage] = useState("main");
  const [repurchaseAmount, setRepurchaseAmount] = useState(null);
  const [dashboardDataByComm, setDashboardDataByComm] = useState({});
  const [isSwitching, setIsSwitching] = useState(false);
  const prevEstateIdRef = useRef(null);
  const expectedFetchCountRef = useRef(0);

  const { userProfile, activeAccount } = useContext(UserProfileContext) || {};

  const isGeneralDashboard =
    activeAccount?.type === "vtu" ||
    getResolvedUserType(userProfile) === USER_TYPES.VTU_USER;

  // Build an effective profile
  let effectiveProfile = userProfile || {};
  if (activeAccount?.type === "estate") {
    const estates = Array.isArray(userProfile?.estates)
      ? userProfile.estates
      : [];
    const match = estates.find(
      (e) =>
        String(e?.estateId?._id || e?.estateId) ===
        String(activeAccount.estateId),
    );
    if (match) {
      effectiveProfile = {
        ...userProfile,
        streetId: { estateId: match.estateId || match },
      };
    }
  }

  const allowedCommodities = getAllowedCommodities(effectiveProfile);

  const sectionOrder = allowedCommodities.filter(
    (commodity) => !!COMMODITY_SECTION_CONFIG[commodity],
  );
  const dispatch = useDispatch();
  const sectionRefs = useRef({});

  useEffect(() => {
    if (isGeneralDashboard) {
      setLoading(false);
      return;
    }

    if (userProfile?._id) {
      const currentEstateId = activeAccount?.estateId || null;
      const isSwitch =
        prevEstateIdRef.current !== null &&
        prevEstateIdRef.current !== currentEstateId;
      prevEstateIdRef.current = currentEstateId;

      const commoditiesToFetch = getAllowedCommodities(effectiveProfile);

      if (isSwitch && commoditiesToFetch.length > 0) {
        // Clear stale data and raise the switching overlay
        setDashboardDataByComm({});
        expectedFetchCountRef.current = commoditiesToFetch.length;
        setIsSwitching(true);
      }

      // Fetch real dashboard data per commodity
      const fetches = commoditiesToFetch.map((commodity) =>
        getDashboardData(
          commodity,
          (data) => {
            const liveRate = parseFloat(data?.rate?.rate);
            if (liveRate > 0) {
              dispatch(setCommodityRates(commodity, liveRate));
            }
            const estateKey = currentEstateId || "default";
            dispatch(setDashboardData(estateKey, commodity, data));
            setDashboardDataByComm((prev) => {
              const next = { ...prev, [commodity]: data };
              if (Object.keys(next).length >= expectedFetchCountRef.current) {
                setIsSwitching(false);
              }
              return next;
            });
          },
          null,
          currentEstateId,
        ),
      );

      Promise.allSettled(fetches).finally(() => setLoading(false));
    }
  }, [userProfile?._id, activeAccount?.estateId, isGeneralDashboard]);

  useEffect(() => {
    // apply initial theme based on the first section
    if (sectionOrder && sectionOrder.length > 0) {
      const initialColor = getCommodityColor(sectionOrder[0], effectiveProfile);
      dispatch(updateBrandColor(initialColor));
    }

    // Observe sections and update theme when they become prominent in viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const comm = Object.keys(sectionRefs.current).find(
              (k) => sectionRefs.current[k] === entry.target,
            );
            if (comm) {
              const color = getCommodityColor(comm, effectiveProfile);
              dispatch(updateBrandColor(color));
            }
          }
        });
      },
      { root: null, threshold: 0.3 },
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [sectionOrder, effectiveProfile, dispatch]);

  return loading ? (
    <FullPageLoader />
  ) : (
    <Box position="relative">
      {isSwitching && (
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={10}
          bg="whiteAlpha.900"
          direction="column"
          align="center"
          justify="center"
          minH="420px"
          borderRadius="md"
        >
          <Spinner size="xl" color="brandColor" speed="0.65s" thickness="3px" />
          <Text mt={4} fontWeight="semibold" color="gray.500" fontSize="15px">
            Switching to {activeAccount?.name || "account"}…
          </Text>
        </Flex>
      )}
      {isGeneralDashboard && (
        <>
          <Box
            color="primary.500"
            fontSize={"20px"}
            fontWeight="bold"
            py={"20px"}
            pl={["5px", "5px", "0"]}
          >
            <Heading>VTU Services</Heading>
          </Box>
          <VTUFeature />
        </>
      )}

      {!isGeneralDashboard && (
        <>
          {displayPage === "main" && (
            <>
              <Box
                color="primary.500"
                fontSize={"20px"}
                fontWeight="bold"
                py={"20px"}
                pl={["5px", "5px", "0"]}
              >
                <DashboardBanner />
              </Box>
              <VStack gap={"6"} align="stretch">
                {sectionOrder.map((commodity) => {
                  const liveData = dashboardDataByComm[commodity] || {};
                  const sectionData = {
                    creditCard: liveData.creditCard || {},
                    rate: liveData.rate || {},
                    lineChart: liveData.lineChart || [],
                    expenses: liveData.expenses || [],
                    meterReading: liveData.meterReading || {},
                  };

                  return (
                    <CommoditySection
                      key={commodity}
                      commodity={commodity}
                      sectionData={sectionData}
                      sectionRef={(el) => (sectionRefs.current[commodity] = el)}
                      setDisplayPage={setDisplayPage}
                      setRepurchaseAmount={setRepurchaseAmount}
                      userProfile={userProfile}
                      estateId={activeAccount?.estateId || null}
                    />
                  );
                })}
              </VStack>
            </>
          )}
          {displayPage === "orderPage" && (
            <ProgressSteps
              setDisplayPage={setDisplayPage}
              displayPage={displayPage}
              userProfile={userProfile}
              initialAmount={repurchaseAmount}
            />
          )}
        </>
      )}
    </Box>
  );
};
